import type { AnimationClip } from '@/types/animationClip'

/** 片段最短时长（秒）；产品约束为至少 1 秒 */
export const MIN_CLIP_DURATION_SEC = 1

/**
 * 按 `startSec` 升序排列 clip 副本。
 */
export function sortClipsByStart(clips: AnimationClip[]): AnimationClip[] {
  return [...clips].sort((a, b) => a.startSec - b.startSec)
}

/**
 * 查找包含全局时刻 `t` 的 clip（区间 `[startSec, startSec + durationSec]` 闭区间，含末端）。
 */
export function findClipContainingGlobalTime(clips: AnimationClip[], t: number): AnimationClip | null {
  const tt = Number(t)
  for (const c of clips) {
    const end = c.startSec + c.durationSec
    if (tt + 1e-9 >= c.startSec && tt <= end + 1e-9) return c
  }
  return null
}

type Gap = { gapStart: number; gapEnd: number }

/**
 * 列出时间轴上未被 clip 覆盖的区间 `[gapStart, gapEnd)`。
 *
 * @param clips - 图层上的 clip（可不排序）
 * @param timelineMaxSec - 工程总时长上限
 */
export function listTimelineGaps(clips: AnimationClip[], timelineMaxSec: number): Gap[] {
  const maxT = Math.max(0, Number(timelineMaxSec))
  const sorted = sortClipsByStart(clips)
  const gaps: Gap[] = []
  let cursor = 0
  for (const c of sorted) {
    if (c.startSec > cursor + 1e-9) gaps.push({ gapStart: cursor, gapEnd: Math.min(maxT, c.startSec) })
    cursor = Math.max(cursor, c.startSec + c.durationSec)
  }
  if (cursor < maxT - 1e-9) gaps.push({ gapStart: cursor, gapEnd: maxT })
  return gaps
}

/**
 * 在包含 `globalTime` 的空档内生成一段新区间（用于「在间隙新建 clip」）。
 *
 * @param preferredDurationSec - 期望时长，会被空档宽度与总时长裁切
 * @returns 新 `startSec` / `durationSec`；无可行空档时返回 `null`
 */
export function proposeClipInGapAtTime(
  clips: AnimationClip[],
  globalTime: number,
  timelineMaxSec: number,
  preferredDurationSec = 2,
): { startSec: number; durationSec: number } | null {
  const maxT = Math.min(600, Math.max(1, timelineMaxSec))
  const t = Math.min(maxT, Math.max(0, Number(globalTime)))
  const gaps = listTimelineGaps(clips, maxT)
  const gap = gaps.find((g) => t >= g.gapStart - 1e-9 && t < g.gapEnd + 1e-9)
  if (!gap) return null
  const pref = Math.max(MIN_CLIP_DURATION_SEC, preferredDurationSec)
  const startSec = Math.max(gap.gapStart, Math.min(t, gap.gapEnd - MIN_CLIP_DURATION_SEC))
  const durationSec = Math.max(
    MIN_CLIP_DURATION_SEC,
    Math.min(pref, gap.gapEnd - startSec, maxT - startSec),
  )
  if (durationSec < MIN_CLIP_DURATION_SEC - 1e-12) return null
  return { startSec, durationSec }
}

/**
 * 计算空档与可见窗口的交集。
 *
 * @param gapStart - 空档起点（秒）
 * @param gapEnd - 空档终点（秒）
 * @param windowStart - 可见窗口起点（秒）
 * @param windowEnd - 可见窗口终点（秒）
 */
export function intersectGapWithWindow(
  gapStart: number,
  gapEnd: number,
  windowStart: number,
  windowEnd: number,
): { gapStart: number; gapEnd: number } | null {
  const start = Math.max(gapStart, windowStart)
  const end = Math.min(gapEnd, windowEnd)
  if (end - start < MIN_CLIP_DURATION_SEC - 1e-9) return null
  return { gapStart: start, gapEnd: end }
}

/**
 * 在可见窗口内提案新 clip 区间。
 *
 * @param windowStartSec - 可见窗口起点（秒）
 * @param windowEndSec - 可见窗口终点（秒）
 * @param preferredDurationSec - 期望时长（秒）
 */
export function proposeClipInGapAtTimeWithinWindow(
  clips: AnimationClip[],
  globalTime: number,
  timelineMaxSec: number,
  windowStartSec: number,
  windowEndSec: number,
  preferredDurationSec = 2,
): { startSec: number; durationSec: number } | null {
  const maxT = Math.min(600, Math.max(1, timelineMaxSec))
  const visibleStart = Math.min(maxT, Math.max(0, windowStartSec))
  const visibleEnd = Math.min(maxT, Math.max(0, windowEndSec))
  if (visibleEnd - visibleStart < MIN_CLIP_DURATION_SEC - 1e-9) return null

  const tRaw = Math.min(maxT, Math.max(0, Number(globalTime)))
  const t = Math.min(visibleEnd, Math.max(visibleStart, tRaw))
  const pref = Math.max(MIN_CLIP_DURATION_SEC, Number(preferredDurationSec) || MIN_CLIP_DURATION_SEC)
  const gaps = listTimelineGaps(clips, maxT)
  for (const gap of gaps) {
    const visibleGap = intersectGapWithWindow(gap.gapStart, gap.gapEnd, visibleStart, visibleEnd)
    if (!visibleGap) continue
    if (t < visibleGap.gapStart - 1e-9 || t > visibleGap.gapEnd + 1e-9) continue
    const startSec = Math.max(
      visibleGap.gapStart,
      Math.min(t, visibleGap.gapEnd - MIN_CLIP_DURATION_SEC),
    )
    const durationSec = Math.max(
      MIN_CLIP_DURATION_SEC,
      Math.min(pref, visibleGap.gapEnd - startSec, maxT - startSec),
    )
    if (durationSec < MIN_CLIP_DURATION_SEC - 1e-12) return null
    return { startSec, durationSec }
  }
  return null
}

/**
 * 将 `startSec` 平移到最近可行值：整段 clip 必须落在「去掉其它 clip 后的空档」内。
 *
 * @param moving - 正在移动的 clip
 * @param newStartSec - 目标起点
 */
export function clampClipStartNonOverlapping(
  clips: AnimationClip[],
  moving: AnimationClip,
  newStartSec: number,
  timelineMaxSec: number,
): number {
  const maxT = Math.min(600, Math.max(1, timelineMaxSec))
  const others = clips.filter((c) => c.id !== moving.id)
  const d = Math.max(MIN_CLIP_DURATION_SEC, moving.durationSec)
  const gaps = listTimelineGaps(others, maxT)
  let best = moving.startSec
  let bestDist = Infinity
  for (const g of gaps) {
    const segLo = g.gapStart
    const segHi = g.gapEnd - d
    if (segHi + 1e-9 < segLo) continue
    const clamped = Math.min(segHi, Math.max(segLo, newStartSec))
    const dist = Math.abs(clamped - newStartSec)
    if (dist < bestDist) {
      bestDist = dist
      best = clamped
    }
  }
  return Number.isFinite(best) ? best : Math.max(0, Math.min(newStartSec, maxT - d))
}

/**
 * 从左侧缩放进：修改起点与时长，右端点尽量保持不变（受邻片与 0 边界约束）。
 */
export function clampResizeClipLeft(
  clips: AnimationClip[],
  moving: AnimationClip,
  newStartSec: number,
  timelineMaxSec: number,
): { startSec: number; durationSec: number } {
  const maxT = Math.min(600, Math.max(1, timelineMaxSec))
  const right = moving.startSec + moving.durationSec
  const others = sortClipsByStart(clips.filter((c) => c.id !== moving.id))
  let minStart = 0
  for (const c of others) {
    const ce = c.startSec + c.durationSec
    if (ce <= moving.startSec + 1e-9) minStart = Math.max(minStart, ce)
  }
  const maxStart = right - MIN_CLIP_DURATION_SEC
  const startSec = Math.min(maxStart, Math.max(minStart, newStartSec))
  const durationSec = Math.max(MIN_CLIP_DURATION_SEC, Math.min(right, maxT) - startSec)
  return { startSec, durationSec }
}

/**
 * 从右侧伸缩：仅改 `durationSec`。
 */
export function clampResizeClipRight(
  clips: AnimationClip[],
  moving: AnimationClip,
  newDurationSec: number,
  timelineMaxSec: number,
): number {
  const maxT = Math.min(600, Math.max(1, timelineMaxSec))
  const start = moving.startSec
  const others = clips.filter((c) => c.id !== moving.id)
  const sorted = sortClipsByStart(others)
  let maxEnd = maxT
  for (const c of sorted) {
    if (c.startSec >= start - 1e-9) {
      maxEnd = Math.min(maxEnd, c.startSec)
      break
    }
  }
  const maxDur = Math.max(MIN_CLIP_DURATION_SEC, maxEnd - start)
  return Math.max(MIN_CLIP_DURATION_SEC, Math.min(maxDur, newDurationSec))
}
